import { fetchWithAuth } from '@/lib/api/core'

export type CloudDispatchState = {
  documentId: string
  title: string
  filePath: string
  agentId: string
  runId?: string | null
  dispatchedAt: string
  targetRepo?: string | null
  prUrl?: string | null
  dispatchStatus: string
  threadId?: string | null
  isActive: boolean
  error?: string
}

/** Minimal fields for CloudDispatchBanner (API row or product-board session state). */
export type CloudDispatchBannerModel = {
  agentId?: string
  title?: string
  prUrl?: string | null
  threadId?: string | null
  isActive?: boolean
  dispatchStatus?: string
  error?: string
}

const TERMINAL_DISPATCH_STATUSES = new Set(['complete', 'error', 'cancelled', 'failed'])

const DOC_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isDocumentUuid(value?: string | null): boolean {
  const t = value?.trim()
  return Boolean(t && DOC_UUID_RE.test(t))
}

export function isActiveCloudDispatch(
  dispatch: Pick<CloudDispatchBannerModel, 'dispatchStatus' | 'isActive'> | null | undefined
): boolean {
  if (!dispatch) return false
  if (typeof dispatch.isActive === 'boolean') return dispatch.isActive
  const status = dispatch.dispatchStatus
  if (!status) return true
  return !TERMINAL_DISPATCH_STATUSES.has(status)
}

export type CloudAgentStatusResponse = {
  success: boolean
  agentId: string
  dispatchStatus: string
  status: string
  branch?: string | null
  prUrl?: string | null
  isActive: boolean
  error?: string
}

export async function fetchCloudAgentStatus(
  agentId: string,
  organizationId?: string | null
): Promise<CloudAgentStatusResponse | null> {
  const q =
    organizationId && organizationId.trim()
      ? `?organizationId=${encodeURIComponent(organizationId.trim())}`
      : ''
  const res = await fetchWithAuth(
    `/api/cursor-cloud/agents/${encodeURIComponent(agentId)}/status${q}`
  )
  const data = await res.json().catch(() => ({}))
  if (!res.ok || !data?.success) return null
  return data as CloudAgentStatusResponse
}

export async function fetchCloudDispatchForDocument(
  documentId?: string | null,
  filePath?: string | null,
  fallbackFilePath?: string | null
): Promise<CloudDispatchState | null> {
  const tryFetch = async (id?: string | null, path?: string | null) => {
    const rawId = id?.trim();
    const docId = isDocumentUuid(rawId) ? rawId! : null;
    const docPath = path?.trim();
    if (!docId && !docPath) return null;
    const params = new URLSearchParams();
    if (docId) params.set('documentId', docId);
    if (docPath) params.set('filePath', docPath);
    const res = await fetchWithAuth(`/api/cursor-cloud/dispatch-for-document?${params.toString()}`)
    if (res.status === 404 || !res.ok) return null
    const data = await res.json()
    if (!data?.success || !data.dispatch) return null
    return data.dispatch as CloudDispatchState
  }

  const primary = await tryFetch(documentId, filePath)
  if (primary) return primary
  if (fallbackFilePath?.trim() && fallbackFilePath.trim() !== filePath?.trim()) {
    return tryFetch(null, fallbackFilePath)
  }
  return null
}

export type DispatchHandoffPayload = {
  handoff_document_id?: string
  handoff_document_path?: string
  target_repo?: 'flownotes' | 'flownotes-content'
  organizationId?: string | null
  inboxOrganizationId?: string | null
}

export async function dispatchHandoffToCloud(payload: DispatchHandoffPayload): Promise<{
  success: boolean
  agentId?: string
  title?: string
  dispatchedAt?: string
  threadId?: string | null
  error?: string
}> {
  const res = await fetchWithAuth('/api/cursor-cloud/dispatch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || !data?.success) {
    return { success: false, error: data?.error || `Dispatch failed (${res.status})` }
  }
  return {
    success: true,
    agentId: data.agentId || data.agent_id,
    title: data.title,
    dispatchedAt: data.dispatchedAt || data.dispatched_at,
    threadId: data.threadId ?? null,
  }
}

export function openInboxThread(threadId: string, title: string, workspaceId?: string | null) {
  window.dispatchEvent(
    new CustomEvent('open-inbox-thread-main', {
      detail: { threadId, title, workspaceId: workspaceId ?? undefined },
    })
  )
}
